/* ==========================================================================
   Stopping Distance Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-sdc]');
  if (!root) return;

  var G_FT_S2 = 32.174;
  var FT_PER_MILE = 5280;
  var SEC_PER_HOUR = 3600;
  var CAR_LENGTH_FT = 15;

  /* Peak friction coefficients for a vehicle with ABS on each surface. */
  var SURFACE_MU = { dry: 0.80, wet: 0.55, packed: 0.28, ice: 0.12 };
  /* Tread depth changes how much of that is available, mostly on wet surfaces. */
  var TIRE_FACTOR = { new: 1.00, good: 0.94, low: 0.82, bald: 0.65 };
  var TIRE_FACTOR_DRY = { new: 1.00, good: 0.98, low: 0.94, bald: 0.88 };

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.speed) || input.speed <= 0) {
      return { ok: false, errors: ['Enter a speed in miles per hour.'] };
    }
    var reaction = isFinite(input.reaction) && input.reaction >= 0 ? input.reaction : 1.5;
    var grade = isFinite(input.grade) ? input.grade / 100 : 0;

    var mu = SURFACE_MU[input.surface] || SURFACE_MU.dry;
    var tf = (input.surface === 'dry' ? TIRE_FACTOR_DRY : TIRE_FACTOR)[input.tires];
    if (!isFinite(tf)) tf = 1;
    var muEff = mu * tf;

    /* Grade adds to or subtracts from the retarding force. */
    var effective = muEff + grade;
    if (effective <= 0.02) {
      return { ok: false, errors: ['With that surface and downhill grade the vehicle cannot stop. Check the inputs.'] };
    }

    var fps = input.speed * FT_PER_MILE / SEC_PER_HOUR;
    var reactionFt = fps * reaction;
    var brakingFt  = (fps * fps) / (2 * G_FT_S2 * effective);
    var totalFt    = reactionFt + brakingFt;
    var brakeTime  = fps / (G_FT_S2 * effective);

    var status;
    if (totalFt < 150) {
      status = { key: 'good', label: num(totalFt, 0) + ' ft',
                 note: 'About ' + num(totalFt / CAR_LENGTH_FT, 1) + ' car lengths.' };
    } else if (totalFt < 300) {
      status = { key: 'monitor', label: num(totalFt, 0) + ' ft',
                 note: 'About ' + num(totalFt / CAR_LENGTH_FT, 1) + ' car lengths. Longer than most drivers picture.' };
    } else if (totalFt < 500) {
      status = { key: 'soon', label: num(totalFt, 0) + ' ft',
                 note: 'About ' + num(totalFt / CAR_LENGTH_FT, 0) + ' car lengths, most of a city block.' };
    } else {
      status = { key: 'now', label: num(totalFt, 0) + ' ft',
                 note: 'Over ' + num(totalFt / CAR_LENGTH_FT, 0) + ' car lengths. Following distance matters far more than braking hardware here.' };
    }

    var warnings = [];
    if (input.tires === 'bald' && input.surface !== 'dry') {
      warnings.push('Worn tires cost far more on a wet surface than a dry one. Tread depth exists to move water out of the contact patch, and there is nothing left to do it with.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Total stopping distance', value: num(totalFt, 0) + ' ft',
          sub: num(totalFt / CAR_LENGTH_FT, 1) + ' car lengths, or ' + num(totalFt * 0.3048, 0) + ' metres.' },
        { label: 'Travelled before you brake', value: num(reactionFt, 0) + ' ft',
          sub: 'At ' + num(input.speed, 0) + ' mph you cover ' + num(fps, 0) + ' feet every second.' }
      ],
      rows: [
        { head: 'Breakdown' },
        { label: 'Reaction distance', value: num(reactionFt, 0) + ' ft' },
        { label: 'Braking distance', value: num(brakingFt, 0) + ' ft' },
        { label: 'Total', value: num(totalFt, 0) + ' ft' },
        { label: 'Time under brakes', value: num(brakeTime, 1) + ' s' },
        { head: 'Conditions' },
        { label: 'Speed', value: num(fps, 0) + ' ft/s' },
        { label: 'Surface grip', value: num(mu, 2) },
        { label: 'Tire factor', value: num(tf, 2) },
        { label: 'Effective grip', value: num(effective, 3) },
        { head: 'For comparison' },
        { label: 'Same stop, dry, new tires',
          value: num(fps * reaction + (fps * fps) / (2 * G_FT_S2 * (SURFACE_MU.dry + grade)), 0) + ' ft' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "speed",
          "key": "s",
          "type": "number"
      },
      {
          "name": "surface",
          "key": "r",
          "type": "select"
      },
      {
          "name": "tires",
          "key": "t",
          "type": "select"
      },
      {
          "name": "reaction",
          "key": "rt",
          "type": "number"
      },
      {
          "name": "grade",
          "key": "g",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-sdc__figure">'
        + '<div class="pc-sdc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-sdc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-sdc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-sdc__row pc-sdc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-sdc__row">'
        + '<span class="pc-sdc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-sdc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_SDC = { compute: compute };
})();
